import React, { useEffect, useMemo, useState } from 'react';
import { ethers } from 'ethers';
import { encodeFunctionData, toFunctionSelector } from 'viem';
import { useAppKitAccount } from '@reown/appkit/react';
import { Landmark, Vote, Clock, Check, X, Play, Ban, Plus, ShieldAlert, ShieldOff, Hourglass } from 'lucide-react';
import { governanceAddress, provider } from '../../.config';
import { shortAddress } from '../../utils';
import { useT } from '../../i18n';
import useGovernance from './useGovernance';
import { GOVERNANCE_TARGETS, describeCall, parseArgument, signatureOf, writableFunctions } from './governanceTargets';

const STATE_TONE = {
  active: 'border-cyan-400/40 bg-cyan-400/10 text-cyan-200',
  succeeded: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  executed: 'border-white/20 bg-white/5 text-white/70',
  defeated: 'border-rose-400/40 bg-rose-400/10 text-rose-300',
  expired: 'border-amber-400/40 bg-amber-400/10 text-amber-300',
  canceled: 'border-white/15 bg-white/5 text-white/45',
  queued: 'border-brand-light/40 bg-brand-light/10 text-brand-soft',
  vetoed: 'border-rose-400/30 bg-rose-400/5 text-rose-300/80',
};

const timeLeft = (until) => {
  const seconds = Math.max(0, until - Math.floor(Date.now() / 1000));
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDate = (timestamp) => new Date(timestamp * 1000).toLocaleDateString();

const RuleTile = ({ label, value }) => (
  <div className="lb-tile-soft p-4">
    <p className="lb-label">{label}</p>
    <p className="notranslate lb-num mt-1 text-lg font-bold text-white">{value}</p>
  </div>
);

/** Yes / no bars as a share of the voting power staked when the proposal was created, with the quorum mark */
const VoteBar = ({ proposal }) => {
  const t = useT();
  const total = Math.max(proposal.snapshotWeight, 1);
  const yesPct = (proposal.yes / total) * 100;
  const noPct = (proposal.no / total) * 100;
  const quorumPct = (proposal.quorum / total) * 100;
  const cast = proposal.yes + proposal.no;
  return (
    <div>
      <div className="relative h-3 overflow-hidden rounded-full bg-white/10">
        <div className="absolute inset-y-0 left-0 bg-emerald-400/80" style={{ width: `${yesPct}%` }} />
        <div className="absolute inset-y-0 bg-rose-400/80" style={{ left: `${yesPct}%`, width: `${noPct}%` }} />
        <div className="absolute inset-y-0 w-0.5 bg-amber-300" style={{ left: `${quorumPct}%` }} title={t('gov.quorum')} />
      </div>
      <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-white/60">
        <span className="notranslate">
          <span className="text-emerald-300">
            {t('gov.yes')} {proposal.yes}
          </span>{' '}
          ·{' '}
          <span className="text-rose-300">
            {t('gov.no')} {proposal.no}
          </span>
        </span>
        <span className="notranslate">
          {t('gov.quorumProgress', {
            cast,
            quorum: proposal.quorum,
            total: proposal.snapshotWeight,
          })}
        </span>
      </div>
    </div>
  );
};

const ProposalCard = ({ proposal, address, gov }) => {
  const t = useT();
  const call = describeCall(proposal.target, proposal.data);
  const isProposer = address && proposal.proposer.toLowerCase() === address.toLowerCase();
  const canVote = proposal.state === 'active' && proposal.votableNodes.length > 0;
  const canVeto = gov.isGuardian && ['active', 'queued', 'succeeded'].includes(proposal.state);

  return (
    <div className="lb-tile p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="lb-label notranslate">#{proposal.id}</p>
          <h3 className="mt-1 break-words text-base font-bold text-white">{proposal.description || '—'}</h3>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
          {proposal.level === 'critical' ? (
            <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.16em] text-amber-200">
              {t('gov.critical')}
            </span>
          ) : null}
          <span
            className={`rounded-full border px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.16em] ${STATE_TONE[proposal.state]}`}
          >
            {t(`gov.state.${proposal.state}`)}
          </span>
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-white/10 bg-black/40 p-3 font-mono text-xs text-white/80">
        <span className="text-amber-200">{call.target ? call.target.label : shortAddress(proposal.target)}</span>
        {call.functionName ? (
          <>
            .<span className="text-cyan-200">{call.functionName}</span>({call.args.join(', ')})
          </>
        ) : (
          <span className="break-all text-white/50"> {proposal.data.slice(0, 74)}…</span>
        )}
      </div>

      <div className="mt-4">
        <VoteBar proposal={proposal} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-white/50">
        <span>
          {t('gov.by')} <span className="notranslate">{shortAddress(proposal.proposer)}</span>
        </span>
        {proposal.state === 'active' ? (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {t('gov.endsIn', { time: timeLeft(proposal.endTime) })}
          </span>
        ) : null}
        {proposal.state === 'queued' ? (
          <span className="inline-flex items-center gap-1 text-brand-soft">
            <Hourglass className="h-3.5 w-3.5" /> {t('gov.executableIn', { time: timeLeft(proposal.executableAt) })}
          </span>
        ) : null}
        {proposal.myVote !== 0 ? (
          <span className={proposal.myVote > 0 ? 'text-emerald-300' : 'text-rose-300'}>
            {proposal.myVote > 0 ? t('gov.youVotedYes') : t('gov.youVotedNo')}
          </span>
        ) : null}
      </div>

      {canVote || canVeto || proposal.state === 'succeeded' || (isProposer && proposal.state === 'active') ? (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-white/5 pt-4">
          {canVote ? (
            <>
              <button className="lb-btn lb-btn-primary lb-btn-sm" onClick={() => gov.vote(proposal.id, proposal.votableNodes, true)}>
                <Check className="mr-1 inline h-3.5 w-3.5" /> {t('gov.voteYes')}
              </button>
              <button className="lb-btn lb-btn-danger lb-btn-sm" onClick={() => gov.vote(proposal.id, proposal.votableNodes, false)}>
                <X className="mr-1 inline h-3.5 w-3.5" /> {t('gov.voteNo')}
              </button>
              <span className="self-center text-xs text-white/50">
                {t('gov.withNodes', {
                  nodes: proposal.votableNodes.map((n) => `#${n}`).join(' '),
                })}
              </span>
            </>
          ) : null}
          {proposal.state === 'succeeded' ? (
            <button className="lb-btn lb-btn-gold lb-btn-sm" onClick={() => gov.execute(proposal.id)}>
              <Play className="mr-1 inline h-3.5 w-3.5" /> {t('gov.execute')}
            </button>
          ) : null}
          {isProposer && proposal.state === 'active' ? (
            <button className="lb-btn lb-btn-outline lb-btn-sm" onClick={() => gov.cancel(proposal.id)}>
              <Ban className="mr-1 inline h-3.5 w-3.5" /> {t('gov.cancel')}
            </button>
          ) : null}
          {canVeto ? (
            <button className="lb-btn lb-btn-danger lb-btn-sm" onClick={() => gov.veto(proposal.id)}>
              <ShieldOff className="mr-1 inline h-3.5 w-3.5" /> {t('gov.veto')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const OWNER_ABI = ['function owner() view returns (address)'];

/** Builds a proposal from the known contracts and their functions */
const ProposalForm = ({ gov, onDone }) => {
  const t = useT();
  const [targetKey, setTargetKey] = useState(GOVERNANCE_TARGETS[0]?.key || '');
  const target = GOVERNANCE_TARGETS.find((item) => item.key === targetKey);
  const allFunctions = useMemo(() => (target ? writableFunctions(target.abi) : []), [target]);
  const [levels, setLevels] = useState({}); // signature -> 'standard' | 'critical' (listed calls only)
  const functions = allFunctions.filter((item) => levels[signatureOf(item)]);
  const [signature, setSignature] = useState('');
  const fn = functions.find((item) => signatureOf(item) === signature) || functions[0];
  const [values, setValues] = useState({});
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState(undefined);

  useEffect(() => {
    setSignature('');
    setValues({});
    setOwner(undefined);
    if (!target) return;
    new ethers.Contract(target.address, OWNER_ABI, provider)
      .owner()
      .then(setOwner)
      .catch(() => setOwner(null));
  }, [target]);

  useEffect(() => setValues({}), [signature]);

  // the governance lists which calls a proposal may make: hide the rest
  const { callLevelOf } = gov;
  useEffect(() => {
    let active = true;
    setLevels({});
    if (!target) return undefined;
    Promise.all(
      allFunctions.map(async (item) => {
        const level = await callLevelOf(target.address, toFunctionSelector(item));
        return [signatureOf(item), level];
      })
    ).then((entries) => {
      if (active) setLevels(Object.fromEntries(entries.filter(([, level]) => level !== 'none')));
    });
    return () => {
      active = false;
    };
  }, [target, allFunctions, callLevelOf]);
  const level = fn ? levels[signatureOf(fn)] : null;

  let encoded = null;
  let encodeError = null;
  if (fn) {
    try {
      const args = fn.inputs.map((input, i) => parseArgument(input.type, values[i] ?? (input.type === 'bool' ? false : '')));
      encoded = encodeFunctionData({ abi: [fn], functionName: fn.name, args });
    } catch (err) {
      encodeError = err.shortMessage || err.message;
    }
  }

  const governed = owner && owner.toLowerCase() === governanceAddress.toLowerCase();

  const submit = async () => {
    await gov.createProposal(target.address, encoded, description.trim() || `${target.label}: ${fn.name}`);
    setDescription('');
    setValues({});
    onDone();
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="lb-label">{t('gov.contract')}</span>
          <select className="lb-input mt-1 py-2" value={targetKey} onChange={(e) => setTargetKey(e.target.value)}>
            {GOVERNANCE_TARGETS.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="lb-label">{t('gov.function')}</span>
          <select
            className="lb-input mt-1 py-2 font-mono text-xs"
            value={fn ? signatureOf(fn) : ''}
            onChange={(e) => setSignature(e.target.value)}
          >
            {functions.map((item) => (
              <option key={signatureOf(item)} value={signatureOf(item)}>
                {signatureOf(item)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {level ? (
        <p
          className={`rounded-xl border p-3 text-xs ${
            level === 'critical' ? 'border-amber-400/30 bg-amber-400/10 text-amber-200' : 'border-white/10 bg-white/5 text-white/60'
          }`}
        >
          {t(level === 'critical' ? 'gov.levelCritical' : 'gov.levelStandard', {
            days: level === 'critical' ? gov.rules?.criticalDays : gov.rules?.timelockDays,
          })}
        </p>
      ) : (
        <p className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/55">{t('gov.noCalls')}</p>
      )}

      {owner !== undefined && !governed ? (
        <p className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          {owner ? t('gov.notOwned', { owner: shortAddress(owner) }) : t('gov.noOwner')}
        </p>
      ) : null}

      {fn && fn.inputs.length ? (
        <div className="grid gap-3 md:grid-cols-2">
          {fn.inputs.map((input, i) => (
            <label key={`${fn.name}-${i}`} className="block">
              <span className="lb-label notranslate">
                {input.name || `arg${i}`} <span className="text-white/35">({input.type})</span>
              </span>
              {input.type === 'bool' ? (
                <select
                  className="lb-input mt-1 py-2"
                  value={String(values[i] ?? false)}
                  onChange={(e) => setValues({ ...values, [i]: e.target.value === 'true' })}
                >
                  <option value="false">false</option>
                  <option value="true">true</option>
                </select>
              ) : (
                <input
                  className="lb-input mt-1 py-2 font-mono text-xs"
                  value={values[i] ?? ''}
                  placeholder={input.type.endsWith(']') ? '[1, 2, 3]' : input.type.startsWith('uint') ? t('gov.rawUnits') : input.type}
                  onChange={(e) => setValues({ ...values, [i]: e.target.value })}
                />
              )}
            </label>
          ))}
        </div>
      ) : null}

      <label className="block">
        <span className="lb-label">{t('gov.description')}</span>
        <textarea
          className="lb-input mt-1 min-h-[80px] py-2"
          value={description}
          maxLength={500}
          placeholder={t('gov.descriptionHint')}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      {encodeError && Object.keys(values).length ? <p className="text-xs text-amber-300">{encodeError}</p> : null}

      <button
        className="lb-btn lb-btn-primary w-full"
        disabled={!encoded || !level || gov.votingPower < (gov.rules?.threshold ?? 1)}
        onClick={submit}
      >
        <Plus className="mr-1 inline h-4 w-4" /> {t('gov.submit')}
      </button>
      {gov.votingPower < (gov.rules?.threshold ?? 1) ? (
        <p className="text-center text-xs text-white/50">
          {t('gov.needPower', {
            threshold: gov.rules?.threshold,
            days: gov.rules?.stakeAgeDays,
          })}
        </p>
      ) : null}
    </div>
  );
};

/** The guardian hands its role to another address (a multisig) for the rest of its term */
const GuardianTransfer = ({ gov }) => {
  const t = useT();
  const [to, setTo] = useState('');
  const valid = ethers.utils.isAddress(to.trim());
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-xl border border-brand-light/30 bg-brand-light/5 p-3 sm:flex-row sm:items-center">
      <p className="text-xs text-white/60 sm:flex-1">{t('gov.guardianTransferHint')}</p>
      <input
        className="lb-input notranslate py-2 font-mono text-xs sm:w-80"
        value={to}
        placeholder="0x…"
        onChange={(e) => setTo(e.target.value)}
      />
      <button className="lb-btn lb-btn-outline lb-btn-sm" disabled={!valid} onClick={() => gov.transferGuardian(to.trim())}>
        {t('gov.guardianTransfer')}
      </button>
    </div>
  );
};

const FILTERS = ['all', 'active', 'queued', 'succeeded', 'closed'];

/** Node council: proposals, voting with staked nodes and execution of approved calls */
const Governance = () => {
  const t = useT();
  const { address, isConnected } = useAppKitAccount();
  const gov = useGovernance(address);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');

  if (!gov.enabled) {
    return (
      <section className="lb-card p-8 text-center text-white/60">
        <Landmark className="mx-auto mb-3 h-10 w-10" />
        {t('gov.notDeployed')}
      </section>
    );
  }

  const shown = gov.proposals.filter((p) =>
    filter === 'all'
      ? true
      : filter === 'closed'
        ? ['executed', 'defeated', 'expired', 'canceled', 'vetoed'].includes(p.state)
        : p.state === filter
  );

  return (
    <div className="space-y-6 text-white">
      <section className="rounded-[32px] border border-white/10 bg-gradient-to-br from-[#0e0b13] via-[#130f19] to-[#1f1729] p-6 sm:p-10">
        <p className="text-sm uppercase tracking-[0.4em] text-amber-300">{t('gov.eyebrow')}</p>
        <h2 className="mt-3 text-3xl font-extrabold uppercase">{t('gov.title')}</h2>
        <p className="mt-3 max-w-3xl text-white/75">{t('gov.blurb')}</p>
        {gov.rules ? (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              <RuleTile label={t('gov.weight')} value={t('gov.weightValue')} />
              <RuleTile
                label={t('gov.quorum')}
                value={t('gov.quorumValue', {
                  quorum: gov.rules.quorum,
                  total: gov.rules.totalWeight,
                })}
              />
              <RuleTile label={t('gov.approval')} value={`≥ ${gov.rules.majorityPct}%`} />
              <RuleTile label={t('gov.voting')} value={t('gov.days', { days: gov.rules.votingDays })} />
              <RuleTile
                label={t('gov.timelock')}
                value={t('gov.timelockValue', {
                  days: gov.rules.timelockDays,
                  critical: gov.rules.criticalDays,
                })}
              />
              <RuleTile label={t('gov.executeWithin')} value={t('gov.days', { days: gov.rules.executionDays })} />
              <RuleTile label={t('gov.stakeAge')} value={t('gov.days', { days: gov.rules.stakeAgeDays })} />
              <RuleTile label={t('gov.toPropose')} value={t('gov.votesValue', { n: gov.rules.threshold })} />
            </div>
            <p className="mt-4 flex items-start gap-2 text-xs text-white/60">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-brand-soft" />
              {gov.rules.guardian
                ? t('gov.guardianInfo', {
                    guardian: shortAddress(gov.rules.guardian),
                    until: formatDate(gov.rules.guardianUntil),
                  })
                : t('gov.noGuardian')}
            </p>
            {gov.isGuardian ? <GuardianTransfer gov={gov} /> : null}
          </>
        ) : null}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/40 p-4">
          <div className="flex items-center gap-3">
            <Vote className="h-6 w-6 text-amber-300" />
            <div>
              <p className="lb-label">{t('gov.yourPower')}</p>
              <p className="notranslate lb-num text-xl font-bold">{isConnected ? gov.votingPower : '—'}</p>
              {isConnected && gov.stakedPower > gov.votingPower ? (
                <p className="text-xs text-white/50">
                  {t('gov.maturing', {
                    n: gov.stakedPower - gov.votingPower,
                    days: gov.rules?.stakeAgeDays,
                  })}
                </p>
              ) : null}
            </div>
          </div>
          <p className="max-w-md text-xs text-white/55">{t('gov.powerHint')}</p>
          <button className="lb-btn lb-btn-gold" onClick={() => setShowForm(!showForm)} disabled={!isConnected}>
            <Plus className="mr-1 inline h-4 w-4" /> {t('gov.newProposal')}
          </button>
        </div>
      </section>

      {showForm ? (
        <section className="lb-card p-6">
          <h3 className="lb-heading mb-4 text-lg">{t('gov.newProposal')}</h3>
          <ProposalForm gov={gov} onDone={() => setShowForm(false)} />
        </section>
      ) : null}

      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="lb-heading text-xl">{t('gov.proposals')}</h3>
          <div className="lb-seg">
            {FILTERS.map((key) => (
              <button key={key} className="lb-seg-item" data-active={filter === key} data-tone="gold" onClick={() => setFilter(key)}>
                {t(`gov.filter.${key}`)}
              </button>
            ))}
          </div>
        </div>
        {gov.loading ? (
          <p className="text-white/50">{t('common.loading')}</p>
        ) : shown.length ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {shown.map((proposal) => (
              <ProposalCard key={proposal.id} proposal={proposal} address={address} gov={gov} />
            ))}
          </div>
        ) : (
          <p className="lb-tile p-6 text-center text-white/50">{t('gov.empty')}</p>
        )}
      </section>
    </div>
  );
};

export default Governance;
